import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  CallClient,
  VideoStreamRenderer,
  LocalVideoStream,
} from '@azure/communication-calling';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import { useLocation } from 'react-router-dom';
import '../components/video/VideoCall.css';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import { useApp } from '../context/AppContext';
import { useMutation } from '@tanstack/react-query';
import { postFeedback } from '../services/FeedbackService';
import { FeedbackFormSheet } from './new/components/bookings/FeedbackFormSheet';
import { ErrorModal } from './new/components/error/ErrorModal';
import { SuccessModal } from '../components/modals/SuccessModal';

const TeamsVideoCall: React.FC = () => {
  const [videoUsername, setVideoUsername] = useState<string>('');
  const [profileName, setProfileName] = useState<string>('');
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [token, setToken] = useState<string>('');
  const [roomId, setRoomId] = useState<string>('');
  const [sendingLocalVideo, setSendingLocalVideo] = useState<boolean>(false);
  const [muted, setMuted] = useState<boolean>(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [call, setCall] = useState<any>(null);
  const [deviceManager, setDeviceManager] = useState<any>(null);
  const [callClient, setCallClient] = useState<any>(null);
  const [callAgent, setCallAgent] = useState<any>(null);
  const [sessionEnded, setSessionEnded] = useState<boolean>(false);
  const [localVideoStream, setLocalVideoStream] = useState<any>(null);
  const [remoteVideoStream, setRemoteVideoStream] = useState<any>(null);
  const [bookingId, setBookingId] = useState<number | null>(null);
  const [professionalId, setProfessionalId] = useState<string>('');
  const [userFeedbackGiven, setUserFeedbackGiven] = useState(false);
  const [professionalFeedbackGiven, setProfessionalFeedbackGiven] =
    useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [rate, setRate] = useState(0);
  const [joinedAt, setJoinedAt] = useState<number | null>(null);
  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const closeAndResetFeedbackForm = () => {
    setShowFeedbackSheet(false);
    setRating(0);
    setComment('');
  };
  const location = useLocation();
  const { profile, token: authToken } = useApp();

  useEffect(() => {
    const storedProfile = localStorage.getItem('profile');
    if (storedProfile) {
      const profile = JSON.parse(storedProfile);
      setProfileName(profile.fullName);
    }
  }, []);

  useEffect(() => {
    const initializeCall = async () => {
      const state = location.state as any;
      if (state) {
        console.log('State retrieved');
        setVideoUsername(state.username);
        setToken(state.token);
        setRoomId(state.videoRoomId);
        setStartTime(new Date(state.startTime));
        setEndTime(new Date(state.endTime));
        setBookingId(state.bookingId ?? null);
        setProfessionalId(state.professionalId ?? '');
        setUserFeedbackGiven(Boolean(state.userFeedbackGiven));
        setProfessionalFeedbackGiven(Boolean(state.professionalFeedbackGiven));
        setAccepted(Boolean(state.accepted));
        setIsCancelled(Boolean(state.isCancelled));
        setPaymentStatus(state.paymentStatus ?? '');
        setRate(Number(state.rate ?? 0));
      }
    };
    initializeCall();
  }, [location.state]);

  useEffect(() => {
    const initialize = async () => {
      if (token) {
        if (!callClient) {
          const callClient = new CallClient();
          const newCallAgent = await callClient.createCallAgent(
            new AzureCommunicationTokenCredential(token)
          );
          setCallClient(callClient);
          setCallAgent(newCallAgent);
        }
      }
    };
    initialize();
    return () => {
      endCall();
      disposeResources();
    };
  }, [token]);

  useEffect(() => {
    if (roomId) {
    }
    return () => {
      endCall();
      disposeResources();
    };
  }, [roomId]);

  useEffect(() => {
    if (callClient) {
      setupCall(callAgent);
    }
    return () => {
      endCall();
      disposeResources();
    };
  }, [callClient]);

  useEffect(() => {
    const calculateRemainingTime = () => {
      if (endTime) {
        const now = new Date();
        const timeDiff = endTime.getTime() - now.getTime();
        const minutesLeft = Math.max(Math.floor(timeDiff / 60000), 0);
        setRemainingTime(minutesLeft);
      }
    };

    const intervalId = setInterval(calculateRemainingTime, 60000);
    calculateRemainingTime();

    return () => clearInterval(intervalId);
  }, [endTime]);

  const hasCurrentUserFeedback = () => {
    if (!profile?.id || !professionalId) {
      return false;
    }

    return profile.id === professionalId
      ? professionalFeedbackGiven
      : userFeedbackGiven;
  };

  const shouldShowCallFeedback = () => {
    if (!joinedAt || !bookingId || hasCurrentUserFeedback()) {
      return false;
    }

    const isPaidOrFree = rate === 0 || paymentStatus === 'succeeded';
    if (!accepted || isCancelled || !isPaidOrFree) {
      return false;
    }

    const callDurationMs = Date.now() - joinedAt;
    return callDurationMs > 10 * 60 * 1000;
  };

  const setupCall = async (callAgent: any) => {
    try {
      const deviceManager = await callClient.getDeviceManager();
      setDeviceManager(deviceManager);
      await deviceManager.askDevicePermission({
        video: true,
        audio: true,
      });
      const roomCallLocator = { roomId };
      const call = callAgent.join(roomCallLocator);
      setCall(call);
      subscribeToCall(call);
    } catch (error) {
      console.error(error);
    }
  };

  const endCall = async () => {
    if (call) {
      await call.hangUp();
      setShowFeedbackSheet(shouldShowCallFeedback());
      setSessionEnded(true);
      setSendingLocalVideo(false);
      setRemoteVideoStream(null);
    }
  };

  const disposeResources = async () => {
    if (call) {
      await call.hangUp();
    }

    if (callAgent) {
      callAgent.dispose();
    }
  };

  const toggleMute = () => {
    setMuted(!muted);
    if (call) {
      if (muted) {
        call.unmute();
      } else {
        call.mute();
      }
    }
  };

  const toggleLocalVideo = async () => {
    setSendingLocalVideo(!sendingLocalVideo);
    try {
      if (sendingLocalVideo) {
        await call.stopVideo(localVideoStream);
        removeLocalVideoStream();
      } else {
        const newLocalVideoStream = await createLocalVideoStream(deviceManager);
        setLocalVideoStream(newLocalVideoStream);
        await call.startVideo(newLocalVideoStream);
        displayLocalVideoStream(newLocalVideoStream);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const createLocalVideoStream = async (deviceManager: any) => {
    const camera = (await deviceManager.getCameras())[0];
    if (camera) {
      return new LocalVideoStream(camera);
    } else {
      console.error(`No camera device found on the system`);
    }
  };

  const subscribeToCall = (call: any) => {
    try {
      call.on('stateChanged', async () => {
        if (call.state === 'Connected') {
          setJoinedAt(Date.now());
        } else if (call.state === 'Disconnected') {
          setShowFeedbackSheet(shouldShowCallFeedback());
          setSessionEnded(true);
        }
      });

      call.localVideoStreams.forEach(async (lvs: any) => {
        await displayLocalVideoStream(lvs);
      });

      call.on('localVideoStreamsUpdated', (e: any) => {
        e.added.forEach(async (lvs: any) => {
          await displayLocalVideoStream(lvs);
        });
        e.removed.forEach((lvs: any) => {
          removeLocalVideoStream();
        });
      });

      call.remoteParticipants.forEach((remoteParticipant: any) => {
        subscribeToRemoteParticipant(remoteParticipant);
      });

      call.on('remoteParticipantsUpdated', (e: any) => {
        e.added.forEach((remoteParticipant: any) => {
          subscribeToRemoteParticipant(remoteParticipant);
        });
        e.removed.forEach((remoteParticipant: any) => {
          console.log('Remote participant removed from the call.');
        });
      });
    } catch (error) {
      console.error(error);
    }
  };

  const subscribeToRemoteParticipant = (remoteParticipant: any) => {
    try {
      remoteParticipant.on('stateChanged', () => {
        console.log(
          `Remote participant state changed: ${remoteParticipant.state}`
        );
      });

      remoteParticipant.videoStreams.forEach((remoteVideoStream: any) => {
        subscribeToRemoteVideoStream(remoteVideoStream);
      });

      remoteParticipant.on('videoStreamsUpdated', (e: any) => {
        e.added.forEach((remoteVideoStream: any) => {
          subscribeToRemoteVideoStream(remoteVideoStream);
        });
        e.removed.forEach((remoteVideoStream: any) => {
          console.log('Remote participant video stream was removed.');
        });
      });
    } catch (error) {
      console.error(error);
    }
  };

  const subscribeToRemoteVideoStream = async (remoteVideoStream: any) => {
    setRemoteVideoStream(remoteVideoStream);
    let renderer = new VideoStreamRenderer(remoteVideoStream);
    let view: any;
    let remoteVideoContainer = document.createElement('div');
    remoteVideoContainer.className = 'remote-video-container';

    const createView = async () => {
      view = await renderer.createView();
      remoteVideoContainer.appendChild(view.target);
      document
        .getElementById('remoteVideosGallery')
        ?.appendChild(remoteVideoContainer);
    };

    remoteVideoStream.on('isAvailableChanged', async () => {
      try {
        if (remoteVideoStream.isAvailable) {
          console.log('Remote video stream is available');
          await createView();
        } else {
          document
            .getElementById('remoteVideosGallery')
            ?.removeChild(remoteVideoContainer);
        }
      } catch (e) {
        console.error(e);
      }
    });

    if (remoteVideoStream.isAvailable) {
      try {
        await createView();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const displayLocalVideoStream = async (localVideoStream: any) => {
    try {
      const localVideoStreamRenderer = new VideoStreamRenderer(
        localVideoStream
      );
      const view = await localVideoStreamRenderer.createView();
      const localVideoContainer = document.getElementById(
        'localVideoContainer'
      );
      if (localVideoContainer) {
        localVideoContainer.innerHTML = '';
        localVideoContainer.appendChild(view.target);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const removeLocalVideoStream = async () => {
    try {
      const localVideoContainer = document.getElementById(
        'localVideoContainer'
      );
      if (localVideoContainer) {
        localVideoContainer.innerHTML = `<div class="initials-circle">${videoUsername.charAt(0)}</div>`;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const isCommentValid = comment.length <= 500;
  const canSubmit = rating >= 1 && rating <= 5 && isCommentValid;

  const {
    mutate: submitFeedback,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      if (!bookingId) {
        throw new Error('Missing bookingId for feedback submission.');
      }

      await postFeedback(
        {
          bookingId,
          rating,
          comment: comment.trim(),
        },
        authToken || ''
      );
    },
    onSuccess: () => {
      setShowFeedbackSheet(false);
      setShowSuccess(true);
      setRating(0);
      setComment('');
    },
    onError: (error) => {
      setShowFeedbackSheet(false);
      console.error('Error submitting call feedback:', error);
    },
  });

  return (
    <NavigationWrapper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#f5f5f5',
          paddingTop: 2,
        }}
      >
        <Box
          sx={{
            width: '90%',
            maxWidth: '1000px',
            height: '700px',
            backgroundColor: 'white',
            padding: 2,
            borderRadius: 1,
            boxShadow: 3,
            position: 'relative',
          }}
        >
          <Typography variant="h6">{videoUsername}</Typography>
          <Typography variant="body1">{remainingTime} mins left</Typography>

          {!remoteVideoStream?.isAvailable && !sessionEnded ? (
            <div className="initials-circle-guest">
              {videoUsername.charAt(0)}
            </div>
          ) : (
            <Box
              id="remoteVideosGallery"
              sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}
            >
              {participants.map((participant, index) => (
                <Box
                  key={index}
                  sx={{ width: '45%', m: 1, backgroundColor: 'black' }}
                >
                  {/* Render participant video or initials */}
                </Box>
              ))}
            </Box>
          )}

          {!sendingLocalVideo && !sessionEnded ? (
            <div className="initials-circle-host">{profileName.charAt(0)}</div>
          ) : (
            !sessionEnded && (
              <Box
                id="localVideoContainer"
                sx={{
                  width: '300px',
                  height: '150px',
                  backgroundColor: 'black',
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  zIndex: 1,
                }}
              >
                {/* Local video stream will be rendered here */}
              </Box>
            )
          )}

          {sessionEnded && (
            <Typography
              variant="h6"
              sx={{ textAlign: 'center', marginTop: 2, color: 'red' }}
            >
              Session Ended
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              bottom: 6,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
            }}
          >
            <Button onClick={toggleMute} sx={{ minWidth: 'auto', padding: 1 }}>
              <img
                src={
                  muted
                    ? '/video-microphone-mute-light.png'
                    : '/video-microphone-light.png'
                }
                alt={muted ? 'Unmute' : 'Mute'}
                style={{ width: 56, height: 56 }}
              />
            </Button>
            <Button
              onClick={toggleLocalVideo}
              sx={{ minWidth: 'auto', padding: 1 }}
            >
              <img
                src={
                  sendingLocalVideo
                    ? '/video-camera-light.png'
                    : '/video-camera-off-light.png'
                }
                alt={sendingLocalVideo ? 'Stop Video' : 'Start Video'}
                style={{ width: 56, height: 56 }}
              />
            </Button>
            <Button
              color="error"
              onClick={endCall}
              sx={{ minWidth: 'auto', padding: 1 }}
            >
              <img
                src={'/video-hangup.png'}
                alt="Hang Up"
                style={{ width: 56, height: 56 }}
              />
            </Button>
          </Box>
        </Box>
      </Box>

      <ErrorModal
        isInError={isError}
        errorMessage="An error has occurred. Please try again."
        dismissAction={() => reset()}
        retryAction={() => submitFeedback()}
        isLoading={isPending}
        testId="teams-call-feedback-error-modal"
      />

      <SuccessModal
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Thank you"
        message="Thank you for your feedback"
      />

      <FeedbackFormSheet
        opened={showFeedbackSheet}
        onClose={closeAndResetFeedbackForm}
        rating={rating}
        onRatingChange={setRating}
        comment={comment}
        onCommentChange={setComment}
        isSubmitting={isPending}
        canSubmit={canSubmit}
        onSubmit={() => submitFeedback()}
        testIdPrefix="teams-call-feedback"
      />
    </NavigationWrapper>
  );
};

export default TeamsVideoCall;
